import React, { useMemo, useState, useRef, useCallback, useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'

import { Button, Modal, Spinner } from 'react-bootstrap'
import ImageAnnotationComponentV2, {
  ImageAnnotationComponentV2Handle,
} from '../../annotation/ImageAnnotationComponentV2'
import { useCreateAnnotationMutation } from '../../../services/annotationApi'
import { useViewContentQuery } from '../../../services/contentApi'
import {
  selectedBBoxClassIndex,
  setBoundingBoxStatus,
  BBoxStatus,
  setSelectedBoundingBox,
  saveStateToHistory,
} from '../../../store/slices/annotationSlice'
import { CreateBBoxAnnotationReq } from '../../../types/api/requests'
import { selectSelectedProjectId } from '../../../store/slices/projectSlice'
import { RootState } from '../../../store'
import { selectSelectedModelId } from '../../../store/slices/modelSlice'
import { AnnotationDataBoundingBox } from '../../../types/models/annotation'
import {
  selectEditingPrediction,
  setEditingPrediction,
  selectActiveConfidenceLevel,
  clearEditingPrediction,
} from '../../../store/slices/predictionSlice'
import { useGetTagsQuery } from '../../../services/tagApi'
import { selectConfidenceThresholds } from '../../../store/slices/uiSettingsSlice'
import { predictionApi, useQueryPredictionsQuery } from '../../../services/predictionApi'

import { QueryPredictionRequest } from '../../../types/prediction'
import { useGetModelQuery } from '../../../services/modelsApi'
import { useGetProjectQuery } from '../../../services/projectsApi'
import { apiTags } from '../../../services/apiTags'
import ErrorDialog from '../../modals/ErrorDialog'
import CanvasTools from '../AnnotationsReview/components/CanvasTools'
import ConfirmDialog from '../../modals/ConfirmDialog'
import SubmitPredictionsModal from '../../modals/SubmitPredictionsModal'
import { predictionToUIBoxes } from '../../annotation/utils/boxTransforms'
import { useInitializeBoxes } from '../../annotation/hooks/useInitializeBoxes'
import ReviewScaffold from '../../annotation/review/ReviewScaffold'
import { usePredictionShortcuts } from '../../annotation/hooks/usePredictionShortcuts'
import { usePredictionReview } from './hooks/usePredictionReview'
import PredictionRightPanel from './PredictionRightPanel'

type PredictionTab = 'inspect' | 'accepted' | 'rejected'

const PredictionReview: React.FC = () => {
  const dispatch = useAppDispatch()
  const [showSubmitModal, setShowSubmitModal] = useState(false)
  const [showNoMoreImagesModal, setShowNoMoreImagesModal] = useState(false)

  // Selecting to keep behavior parity with other selectors; not directly used after tab scoping
  const annotationRef = useRef<ImageAnnotationComponentV2Handle>(null)

  const [createAnnotation] = useCreateAnnotationMutation()

  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const { data: project } = useGetProjectQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })
  const selectedModelId = useAppSelector(selectSelectedModelId)
  const editingPrediction = useAppSelector(selectEditingPrediction)

  const selectedPredictions = useAppSelector(
    (state: RootState) => state.prediction.selectedPredictions
  )
  const selectedBBoxClassIdx = useAppSelector(selectedBBoxClassIndex)

  // moved to usePredictionReview

  const [showBBoxes, setShowBBoxes] = useState(true)
  const [zoomLevel, setZoomLevel] = useState(1)
  const [showError, setShowError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)

  const [bboxThreshold] = useState(0.01)

  const [showConfirmUnannotated, setShowConfirmUnannotated] = useState(false)
  // Centralized review state and helpers
  const {
    // state
    setSelectedSnippetIndex,
    hoveredBoxIndex,
    setHoveredBoxIndex,
    activeRightTab,
    setActiveRightTab,
    // selectors
    allBoundingBoxes,
    allUntouchedBBoxes,
    acceptedBBoxes,
    rejectedBBoxes,
    // helpers
    resetForNextImage,
    getCurrentSelectedBox,
  } = usePredictionReview()

  const { data: selectedModel } = useGetModelQuery(selectedModelId || '', {
    skip: !selectedModelId,
  })

  const queryParams: QueryPredictionRequest = useMemo(
    () => ({
      query: {
        filters: [{ key: 'modelid', value: selectedModel?.id || '' }],
        sort_key: 'contentid',
        sort_val: -1,
        operator: 'and',
        page: 0,
        limit: 1000,
      },
      filter_annotated_content: {
        enable: true,
        projectid: selectedProjectId || '',
        datasetid: project?.datasetid || '',
      },
      uncertainty_threshold: bboxThreshold,
      ...(selectedModel?.batch?.metadata_filter?.datasource &&
      selectedModel?.batch?.metadata_filter?.datasource.length > 0
        ? {
            metadata_filter: {
              datasource: selectedModel?.batch?.metadata_filter?.datasource,
            },
          }
        : {}),
    }),
    [
      selectedProjectId,
      project?.datasetid,
      selectedModel?.id,
      bboxThreshold,
      selectedModel?.batch?.metadata_filter?.datasource,
    ]
  )

  const { data: allPredictions } = useQueryPredictionsQuery(queryParams, {
    skip: !selectedModelId,
  })

  const confidenceRanges = useAppSelector(selectConfidenceThresholds)
  const activeConfidenceLevel = useAppSelector(selectActiveConfidenceLevel)

  const { data: tags } = useGetTagsQuery(
    {
      project_id: selectedProjectId || '',
      dataset_id: project?.datasetid || '',
    },
    { skip: !selectedProjectId || !project?.datasetid }
  )

  const { data: contentData } = useViewContentQuery(
    {
      ContentID: editingPrediction?.contentid || '',
      project_id: selectedProjectId || '',
      dataset_id: project?.datasetid || '',
    },
    { skip: !editingPrediction }
  )

  // moved to hook: allBoundingBoxes and getCurrentSelectedBox

  const baseBoxesForTab = useMemo(() => {
    if (activeRightTab === 'accepted') return acceptedBBoxes
    if (activeRightTab === 'rejected') return rejectedBBoxes
    return allUntouchedBBoxes
  }, [activeRightTab, acceptedBBoxes, rejectedBBoxes, allUntouchedBBoxes])

  // Derive focused index relative to the current tab list to keep canvas/snippet alignment stable
  const focusedIndexForTab = useMemo(() => {
    if (selectedBBoxClassIdx == null) return undefined
    const list = baseBoxesForTab
    const idx = list.findIndex((b) => b.class_index === selectedBBoxClassIdx)
    return idx >= 0 ? idx : undefined
  }, [baseBoxesForTab, selectedBBoxClassIdx])

  // If the current tab becomes empty, switch to the next tab that has items
  useEffect(() => {
    const counts: Record<PredictionTab, number> = {
      inspect: allUntouchedBBoxes.length,
      accepted: acceptedBBoxes.length,
      rejected: rejectedBBoxes.length,
    }
    if (counts[activeRightTab as PredictionTab] === 0) {
      const order: PredictionTab[] = ['inspect', 'accepted', 'rejected']
      const startIdx = order.indexOf(activeRightTab as PredictionTab)
      for (let i = 1; i <= 2; i++) {
        const nextTab = order[(startIdx + i) % order.length]
        if (counts[nextTab] > 0) {
          setActiveRightTab(nextTab)
          break
        }
      }
    }
  }, [
    activeRightTab,
    allUntouchedBBoxes.length,
    acceptedBBoxes.length,
    rejectedBBoxes.length,
    setActiveRightTab,
  ])

  // Build UI boxes from predictions via shared transform and seed Redux via hook
  const uiBoxes = useMemo(() => {
    if (!editingPrediction || !tags?.tags || !contentData) return []
    if (
      project?.annotation_type === 'bounding_box' ||
      project?.annotation_type === 'oriented_bounding_box'
    ) {
      const boxes = predictionToUIBoxes(
        [...editingPrediction.predictions].sort(
          (a, b) => (b.confidence ?? 0) - (a.confidence ?? 0)
        ),
        tags.tags,
        contentData.width,
        contentData.height,
        project.annotation_type,
        0
      )
      // Reset selection state before updating boxes
      setSelectedSnippetIndex(undefined)
      dispatch(setSelectedBoundingBox(undefined))
      return boxes
    }
    // Classification: synthesize UI items without geometry so right panel can render badges/buttons
    if (project?.annotation_type === 'classification') {
      const boxes = [...editingPrediction.predictions]
        .sort((a, b) => (b.confidence ?? 0) - (a.confidence ?? 0))
        .map((p) => ({
          name: p.class_name, // classification uses class_name, tagid is empty
          points: [], // no geometry for classification
          ai_labeled: true,
          confidence: p.confidence,
          class_index: p.class_index,
          class_name: p.class_name,
        }))
      setSelectedSnippetIndex(undefined)
      dispatch(setSelectedBoundingBox(undefined))
      return boxes as unknown as AnnotationDataBoundingBox[]
    }
    return []
  }, [
    editingPrediction,
    tags,
    contentData,
    project?.annotation_type,
    dispatch,
    setSelectedSnippetIndex,
  ])

  useInitializeBoxes({ mode: 'prediction', boxes: uiBoxes })

  const filteredPredictionsByConfidence = useMemo(() => {
    if (!allPredictions?.predictions) return []

    switch (activeConfidenceLevel) {
      case 'high':
        return allPredictions.predictions.filter((prediction) => {
          const maxConfidence = Math.max(
            ...prediction.predictions.map((pred) => pred.confidence || 0)
          )
          return maxConfidence >= confidenceRanges.high
        })
      case 'medium':
        return allPredictions.predictions.filter((prediction) => {
          const maxConfidence = Math.max(
            ...prediction.predictions.map((pred) => pred.confidence || 0)
          )
          return maxConfidence >= confidenceRanges.medium && maxConfidence < confidenceRanges.high
        })
      case 'low':
        return allPredictions.predictions.filter((prediction) => {
          const maxConfidence = Math.max(
            ...prediction.predictions.map((pred) => pred.confidence || 0)
          )
          return maxConfidence < confidenceRanges.medium && maxConfidence >= confidenceRanges.low
        })
      case 'none':
        return allPredictions.predictions.filter((prediction) => {
          return prediction.predictions.length === 0
        })
      default:
        return []
    }
  }, [allPredictions?.predictions, activeConfidenceLevel, confidenceRanges])

  const handleNextImage = useCallback(() => {
    if (!editingPrediction || !filteredPredictionsByConfidence.length) return
    const currentIndex = filteredPredictionsByConfidence.findIndex(
      (p) => p.id === editingPrediction.id
    )
    if (currentIndex > -1 && currentIndex < filteredPredictionsByConfidence.length - 1) {
      const nextPrediction = filteredPredictionsByConfidence[currentIndex + 1]

      // Reset all states before updating prediction
      resetForNextImage()

      // Update prediction data
      dispatch(setEditingPrediction(nextPrediction))
    } else {
      setShowNoMoreImagesModal(true)
    }
    setActiveRightTab('inspect')
  }, [
    editingPrediction,
    filteredPredictionsByConfidence,
    dispatch,
    resetForNextImage,
    setActiveRightTab,
  ])

  const handlePrevImage = useCallback(() => {
    if (!editingPrediction || !filteredPredictionsByConfidence.length) return
    const currentIndex = filteredPredictionsByConfidence.findIndex(
      (p) => p.id === editingPrediction.id
    )
    if (currentIndex > 0) {
      const prevPrediction = filteredPredictionsByConfidence[currentIndex - 1]

      // Reset all states before updating prediction
      resetForNextImage()

      // Update prediction data
      dispatch(setEditingPrediction(prevPrediction))
    }
    setActiveRightTab('inspect')
  }, [
    editingPrediction,
    filteredPredictionsByConfidence,
    dispatch,
    resetForNextImage,
    setActiveRightTab,
  ])

  // Centralized prediction keyboard shortcuts
  usePredictionShortcuts({
    isActive: !!editingPrediction && !isProcessing && !showSubmitModal && !showConfirmUnannotated,
    isResizing: () =>
      (annotationRef.current as ImageAnnotationComponentV2Handle | null)?.isResizing?.() ?? false,
    allBoxes: allBoundingBoxes,
    getCurrentSelectedBox,
    acceptBox: (box: AnnotationDataBoundingBox) => handleAcceptBoxKey(box),
    rejectBox: (box: AnnotationDataBoundingBox) => handleRejectBoxKey(box),
    onNextImage: handleNextImage,
    onPrevImage: handlePrevImage,
    onSubmitIfDone: () => {
      if (acceptedBBoxes.length > 0 || allUntouchedBBoxes.length > 0) {
        setShowSubmitModal(true)
      } else {
        setShowConfirmUnannotated(true)
      }
    },
  })

  const handleAcceptPredictions = useCallback(async () => {
    if (isProcessing) return
    setIsProcessing(true)
    if (editingPrediction) {
      try {
        const toSubmit = [...acceptedBBoxes, ...allUntouchedBBoxes]
        const annotationTagIds: string[] = Array.from(new Set(toSubmit.map((a) => a.name)))
        const annoToCreate: CreateBBoxAnnotationReq = {
          content_id: editingPrediction.contentid || '',
          dataset_id: project?.datasetid || '',
          metadata:
            project?.annotation_type === 'bounding_box'
              ? { bounding_boxes: toSubmit }
              : project?.annotation_type === 'oriented_bounding_box'
              ? { oriented_bounding_boxes: toSubmit }
              : undefined,
          project_id: selectedProjectId || '',
          tag_id: annotationTagIds,
          model_id: selectedModelId || '',
          ai_labeled: toSubmit.some((b) => b.ai_labeled !== undefined && b.ai_labeled !== null),
          image_width: contentData?.width,
          image_height: contentData?.height,
        }
        await createAnnotation(annoToCreate)
        // Refetch predictions to update the list
        dispatch(predictionApi.util.invalidateTags([apiTags.PredictionList, apiTags.Prediction]))
      } catch (error: unknown) {
        setShowError(true)
        const apiMessage = (error as { data?: { message?: string } } | undefined)?.data?.message
        const message = apiMessage || 'Unknown Error'
        setErrorMessage('Failed to create annotation: ' + message)
      }
      handleNextImage()
    } else {
      for (const prediction of selectedPredictions) {
        try {
          await createAnnotation({
            content_id: prediction.contentid,
            dataset_id: project?.datasetid || '',
            project_id: selectedProjectId || '',
            tag_id: prediction.predictions.map((p) => p.tagid),
            model_id: selectedModelId || '',
            ai_labeled: prediction.predictions.some(
              (p) => p.confidence !== undefined && p.confidence !== null && p.confidence > 0
            ),
            image_width: contentData?.width,
            image_height: contentData?.height,
            metadata:
              project?.annotation_type === 'bounding_box'
                ? {
                    bounding_boxes: prediction.predictions.flatMap((p) =>
                      p.bounding_boxes
                        ? Object.values(p.bounding_boxes)
                        : p.oriented_bounding_boxes
                        ? Object.values(p.oriented_bounding_boxes)
                        : []
                    ),
                  }
                : project?.annotation_type === 'oriented_bounding_box'
                ? {
                    oriented_bounding_boxes: prediction.predictions.flatMap((p) =>
                      p.oriented_bounding_boxes
                        ? Object.values(p.oriented_bounding_boxes)
                        : p.bounding_boxes
                        ? Object.values(p.bounding_boxes)
                        : []
                    ),
                  }
                : undefined,
          })
          dispatch(predictionApi.util.invalidateTags([apiTags.PredictionList, apiTags.Prediction]))
        } catch (error: unknown) {
          setShowError(true)
          const apiMessage = (error as { data?: { message?: string } } | null)?.data?.message
          const message = apiMessage || 'Unknown Error'
          setErrorMessage('Failed to create annotation: ' + message)
        }
      }
    }
    setIsProcessing(false)
  }, [
    editingPrediction,
    handleNextImage,
    acceptedBBoxes,
    allUntouchedBBoxes,
    project?.datasetid,
    project?.annotation_type,
    selectedProjectId,
    selectedModelId,
    contentData?.width,
    contentData?.height,
    createAnnotation,
    dispatch,
    selectedPredictions,
    isProcessing,
  ])

  const handleZoomIn = () => {
    const newZoom = Math.min(zoomLevel * 1.2, 5)
    setZoomLevel(newZoom)
    if (annotationRef.current) {
      annotationRef.current.setZoom(newZoom)
    }
  }

  const handleZoomOut = () => {
    const newZoom = Math.max(zoomLevel / 1.2, 0.2)
    setZoomLevel(newZoom)
    if (annotationRef.current) {
      annotationRef.current.setZoom(newZoom)
    }
  }

  const handleResetZoom = () => {
    annotationRef.current?.recenterImage?.()
  }

  const handleUndo = () => {
    annotationRef.current?.undo()
  }

  const handleRedo = () => {
    annotationRef.current?.redo()
  }

  const handleDelete = () => {
    annotationRef.current?.delete()
  }

  // const handleDrawBox = () => {
  //   setIsDrawing(!isDrawing)
  // }

  // Updated accept and reject handlers using the new setBoundingBoxStatus action
  const handleAcceptBoxKey = useCallback(
    (box: AnnotationDataBoundingBox, e?: React.MouseEvent | KeyboardEvent) => {
      e?.stopPropagation()
      dispatch(saveStateToHistory())

      // Set the box status to accepted
      dispatch(
        setBoundingBoxStatus({
          classIndex: box.class_index,
          status: BBoxStatus.Accepted,
        })
      )
      // Only advance selection if this action moves the box out of the current list
      if (activeRightTab !== 'accepted') {
        const currentList = activeRightTab === 'rejected' ? rejectedBBoxes : allUntouchedBBoxes
        const currentIndex = currentList.findIndex((b) => b.class_index === box.class_index)
        if (currentIndex !== -1) {
          const newListLen = currentList.length - 1
          if (newListLen > 0) {
            // Select neighbor within the same list (positional in current tab)
            const newList = currentList.filter((b) => b.class_index !== box.class_index)
            const targetIndex = Math.min(currentIndex, newList.length - 1)
            const nextBox = newList[targetIndex]
            dispatch(setSelectedBoundingBox(nextBox))
            setSelectedSnippetIndex(targetIndex)
            // Zoom/pan to the next selection for continuity
            annotationRef.current?.zoomToBBox(nextBox)
          } else {
            // List became empty; switch to the next tab over that has items
            dispatch(setSelectedBoundingBox(undefined))
            setSelectedSnippetIndex(undefined)
            const tabOrder: PredictionTab[] = ['inspect', 'accepted', 'rejected']
            const currentIdx = tabOrder.indexOf(activeRightTab)
            // Calculate lengths after this accept action
            const inspectLenAfter =
              allUntouchedBBoxes.length - (activeRightTab === 'inspect' ? 1 : 0)
            const acceptedLenAfter = acceptedBBoxes.length + 1
            const rejectedLenAfter = rejectedBBoxes.length - (activeRightTab === 'rejected' ? 1 : 0)
            const lenByTab: Record<PredictionTab, number> = {
              inspect: inspectLenAfter,
              accepted: acceptedLenAfter,
              rejected: rejectedLenAfter,
            }
            for (let k = 1; k <= 2; k++) {
              const nextTab = tabOrder[(currentIdx + k) % tabOrder.length]
              if (lenByTab[nextTab] > 0) {
                setActiveRightTab(nextTab)
                break
              }
            }
          }
        }
      }
    },
    [
      dispatch,
      activeRightTab,
      acceptedBBoxes,
      rejectedBBoxes,
      allUntouchedBBoxes,
      setSelectedSnippetIndex,
      setActiveRightTab,
    ]
  )

  const handleRejectBoxKey = useCallback(
    (box: AnnotationDataBoundingBox, e?: React.MouseEvent | KeyboardEvent) => {
      e?.stopPropagation()
      dispatch(saveStateToHistory())
      // Set the box status to rejected
      dispatch(
        setBoundingBoxStatus({
          classIndex: box.class_index,
          status: BBoxStatus.Rejected,
        })
      )
      // Only advance selection if this action moves the box out of the current list
      if (activeRightTab !== 'rejected') {
        const currentList = activeRightTab === 'accepted' ? acceptedBBoxes : allUntouchedBBoxes
        const currentIndex = currentList.findIndex((b) => b.class_index === box.class_index)
        if (currentIndex !== -1) {
          const newListLen = currentList.length - 1
          if (newListLen > 0) {
            // Select neighbor within the same list (positional in current tab)
            const newList = currentList.filter((b) => b.class_index !== box.class_index)
            const targetIndex = Math.min(currentIndex, newList.length - 1)
            const nextBox = newList[targetIndex]
            dispatch(setSelectedBoundingBox(nextBox))
            setSelectedSnippetIndex(targetIndex)
            // Zoom/pan to the next selection for continuity
            annotationRef.current?.zoomToBBox(nextBox)
          } else {
            // List became empty; switch to the next tab over that has items
            dispatch(setSelectedBoundingBox(undefined))
            setSelectedSnippetIndex(undefined)
            const tabOrder: PredictionTab[] = ['inspect', 'accepted', 'rejected']
            const currentIdx = tabOrder.indexOf(activeRightTab)
            // Calculate lengths after this reject action
            const inspectLenAfter =
              allUntouchedBBoxes.length - (activeRightTab === 'inspect' ? 1 : 0)
            const acceptedLenAfter = acceptedBBoxes.length - (activeRightTab === 'accepted' ? 1 : 0)
            const rejectedLenAfter = rejectedBBoxes.length + 1
            const lenByTab: Record<PredictionTab, number> = {
              inspect: inspectLenAfter,
              accepted: acceptedLenAfter,
              rejected: rejectedLenAfter,
            }
            for (let k = 1; k <= 2; k++) {
              const nextTab = tabOrder[(currentIdx + k) % tabOrder.length]
              if (lenByTab[nextTab] > 0) {
                setActiveRightTab(nextTab)
                break
              }
            }
          }
        }
      }
    },
    [
      dispatch,
      activeRightTab,
      acceptedBBoxes,
      rejectedBBoxes,
      allUntouchedBBoxes,
      setSelectedSnippetIndex,
      setActiveRightTab,
    ]
  )

  const handleSelectTab = (key: string | null) => {
    const nextTab: PredictionTab =
      key === 'accepted' ? 'accepted' : key === 'rejected' ? 'rejected' : 'inspect'
    setActiveRightTab(nextTab)
    dispatch(setSelectedBoundingBox(undefined))
    setSelectedSnippetIndex(undefined)
    annotationRef.current?.recenterImage?.()
  }

  // Handler for clicking a snippet in the Inspect tab
  const handleSnippetClick = (box: AnnotationDataBoundingBox) => {
    if (
      project?.annotation_type === 'bounding_box' ||
      project?.annotation_type === 'oriented_bounding_box'
    ) {
      annotationRef.current?.zoomToBBox(box)
    }
    const idx = allBoundingBoxes.findIndex((b) => b.class_index === box.class_index)
    setSelectedSnippetIndex(idx >= 0 ? idx : undefined)
    // For classification, selection is only for list highlighting; canvas selection is harmless
    dispatch(setSelectedBoundingBox(box))
  }

  return (
    <>
      <ReviewScaffold
        left={
          <CanvasTools
            onBackToGallery={() => dispatch(clearEditingPrediction())}
            onPrevious={handlePrevImage}
            onNext={handleNextImage}
            onSubmit={() => setShowSubmitModal(true)}
            hasAnnotations={acceptedBBoxes.length > 0 || allUntouchedBBoxes.length > 0}
            showConfirmDialog={() => {
              setShowConfirmUnannotated(true)
            }}
            activeTab={'predictions'}
            onModelSelect={() => {
              /* no-op for predictions review */
            }}
            onRunInference={() => {
              /* no-op for predictions review */
            }}
            inferenceLoading={false}
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
            onResetZoom={handleResetZoom}
            onUndo={handleUndo}
            onRedo={handleRedo}
            onDelete={handleDelete}
            showDelete={false}
            showBBoxes={showBBoxes}
            onToggleBBoxes={() => setShowBBoxes(!showBBoxes)}
            datasetName={contentData?.metadata?.datasource}
          />
        }
        center={
          editingPrediction ? (
            <div className="position-relative w-100">
              {isProcessing && (
                <div
                  className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-dark bg-opacity-50"
                  style={{ zIndex: 10 }}
                >
                  <div className="text-center">
                    <Spinner animation="border" variant="light" />
                    <div className="mt-2 text-light">Submitting annotations…</div>
                  </div>
                </div>
              )}
              <ImageAnnotationComponentV2
                ref={annotationRef}
                contentId={editingPrediction.contentid}
                useBBoxes={
                  project?.annotation_type === 'bounding_box' ||
                  project?.annotation_type === 'oriented_bounding_box'
                }
                focusedBBoxIndex={focusedIndexForTab}
                // setFocusedBBoxIndex={handleSetFocusedBBoxIndex}
                showBBoxes={showBBoxes}
                filteredBoxes={baseBoxesForTab}
                zoomLevel={zoomLevel}
                onZoomChange={setZoomLevel}
                hoveredBoxIndex={hoveredBoxIndex}
                showConfidence={true}
                labelMode={'class+confidence'}
                isEditable={
                  project?.annotation_type === 'bounding_box' ||
                  project?.annotation_type === 'oriented_bounding_box'
                }
                selectOnFocus={true}
              />
            </div>
          ) : null
        }
        right={
          editingPrediction ? (
            <PredictionRightPanel
              contentId={editingPrediction.contentid}
              activeTab={activeRightTab}
              onSelectTab={handleSelectTab}
              untouched={allUntouchedBBoxes}
              accepted={acceptedBBoxes}
              rejected={rejectedBBoxes}
              selectedBBoxClassIndex={selectedBBoxClassIdx ?? undefined}
              onSnippetSelect={handleSnippetClick}
              onAccept={handleAcceptBoxKey}
              onReject={handleRejectBoxKey}
              hoveredBoxIndex={hoveredBoxIndex}
              onHover={setHoveredBoxIndex}
            />
          ) : null
        }
      />
      <SubmitPredictionsModal
        show={showSubmitModal}
        onHide={() => setShowSubmitModal(false)}
        onConfirm={() => {
          handleAcceptPredictions()
          setShowSubmitModal(false)
        }}
        inspectCount={allUntouchedBBoxes.length}
        acceptedCount={acceptedBBoxes.length}
        rejectedCount={rejectedBBoxes.length}
      />
      <Modal show={showNoMoreImagesModal} onHide={() => setShowNoMoreImagesModal(false)} centered>
        <Modal.Header closeButton className="bg-dark text-light border-secondary">
          <Modal.Title>No More Images</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-dark text-light">
          <p>You have reached the end of the available images to review.</p>
        </Modal.Body>
        <Modal.Footer className="bg-dark border-secondary">
          <Button variant="secondary" onClick={() => setShowNoMoreImagesModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      <ConfirmDialog
        show={showConfirmUnannotated}
        onHide={() => setShowConfirmUnannotated(false)}
        onConfirmRight={() => handleAcceptPredictions()}
        title={'Create a Null Annotation'}
        message={
          'Proceeding will make this image a null annotation. To keep this image in Content as an unannotated image cancel and select the next image.'
        }
        variant="primary"
        confirmRightText={'Make Null'}
        cancelText="Cancel"
      />
      <ErrorDialog
        show={showError}
        onHide={() => setShowError(false)}
        message={errorMessage}
        title="Prediction Review Error"
      />
    </>
  )
}

export default PredictionReview
